
import {
  labelMedian,
  labelTopQuartile,
  labelAverage,
  labelYourCompany,
  medianColor,
  topQuartileColor,
  yourCompanyColor
} from 'utils/constants';
import { ChartFilters, ChartSwitches } from 'interfaces/store/chartFilters';
import {
  allValuesAreNull,
  applyFiltersToChartData,
  ChartDataMap,
  CompanyMetrics,
  generateBubbleData,
  generateBubbleDataCompany,
  getARRIndex,
  getAvgDealSizeIndex,
  mapCalculatedData,
  NSIZE_10,
  NSIZE_8,
  sortDir,
  yourCompanyFormatted
} from './chartsDataHelpers';

// Adjusts a percentage value by removing another percentage from the total

function adjustPercentage(value: number, removed: number): number {
  if (removed === 0) return value; // no adjustment needed
  if (removed >= 100) {
    throw new Error("Removed value cannot be 100% or more.");
  }
  const remainingTotal = 100 - removed;
  return Number(((value / remainingTotal) * 100).toFixed(2));
}


// generate datasets for the graphs
export const mapChartData = (
  chartData: ChartDataMap[],
  companyMetrics: CompanyMetrics,
  chartFilters: ChartFilters,
  chartSwitches: ChartSwitches
) => {
  const xAxisSwitch = chartSwitches.xAxisSwitch;

  const arrIndex = xAxisSwitch
    ? getARRIndex(companyMetrics.currentARR || companyMetrics.revenue || 0)
    : getAvgDealSizeIndex(companyMetrics.avgDealSize || 0);

  let arrGrowthMap = new Map<string, number[]>();
  let netDollarRetentionMap = new Map<string, number[]>();
  let grossDollarRetentionMap = new Map<string, number[]>();
  let netMagicNumberMap = new Map<string, number[]>();
  let grossMarginMap = new Map<string, number[]>();
  let ruleof40Map = new Map<string, number[]>();
  let burnMultipleMap = new Map<string, number[]>();
  let ARRperFTEMap = new Map<string, number[]>();
  let OpExMapRD = new Map<string, number[]>();
  let OpExMapSM = new Map<string, number[]>();
  let OpExMapGA = new Map<string, number[]>();
  let fcfMarginMap = new Map<string, number[]>();
  let quickRatioMap = new Map<string, number[]>();
  let headCountDistributionMapRD = new Map<string, number[]>();
  let headCountDistributionMapSM = new Map<string, number[]>();
  let headCountDistributionMapGA = new Map<string, number[]>();
  let grossNewARRExpansion = new Map<string, number[]>();
  let grossNewARRLogo = new Map<string, number[]>();
  let newLogoVelocityMap = new Map<string, number[]>();
  let opexPerFTEMap = new Map<string, number[]>();
  let cacPaybackMap = new Map<string, number[]>();

  const filteredChartData = applyFiltersToChartData(
    chartData,
    chartFilters,
    chartSwitches
  );

  const filterField = xAxisSwitch ? 'ARR_RANGE' : 'AVG_DEAL_SIZE';

  filteredChartData.forEach((chart: ChartDataMap) => {
    if (chart.ARR_YYGROWTH_EOP !== null) {
      arrGrowthMap.set(chart.ARR_RANGE, [
        ...(arrGrowthMap.get(chart.ARR_RANGE) || []),
        chart.ARR_YYGROWTH_EOP
      ]);
    }

    if (chart.RETENTION_NET_ARR_ANNUALIZED !== null) {
      netDollarRetentionMap.set(chart[filterField], [
        ...(netDollarRetentionMap.get(chart[filterField]) || []),
        chart.RETENTION_NET_ARR_ANNUALIZED
      ]);
    }

    if (chart.MAGICNUMBER_NET !== null) {
      netMagicNumberMap.set(chart[filterField], [
        ...(netMagicNumberMap.get(chart[filterField]) || []),
        chart.MAGICNUMBER_NET
      ]);
    }

    if (chart.MARGIN_GROSSPROFIT !== null) {
      grossMarginMap.set(chart.ARR_RANGE, [
        ...(grossMarginMap.get(chart.ARR_RANGE) || []),
        chart.MARGIN_GROSSPROFIT
      ]);
    }

    if (chart.RULE_OF_40 !== null) {
      ruleof40Map.set(chart.ARR_RANGE, [
        ...(ruleof40Map.get(chart.ARR_RANGE) || []),
        chart.RULE_OF_40
      ]);
    }

    if (chart.BURN_MULTIPLE !== null) {
      burnMultipleMap.set(chart.ARR_RANGE, [
        ...(burnMultipleMap.get(chart.ARR_RANGE) || []),
        chart.BURN_MULTIPLE
      ]);
    }

    if (chart.ARR_PER_FTE !== null) {
      ARRperFTEMap.set(chart.ARR_RANGE, [
        ...(ARRperFTEMap.get(chart.ARR_RANGE) || []),
        chart.ARR_PER_FTE
      ]);
    }

    if (chart.MARGIN_OPEX_RD !== null) {
      OpExMapRD.set(chart.ARR_RANGE, [
        ...(OpExMapRD.get(chart.ARR_RANGE) || []),
        chart.MARGIN_OPEX_RD
      ]);
    }

    if (chart.MARGIN_OPEX_SM !== null) {
      OpExMapSM.set(chart.ARR_RANGE, [
        ...(OpExMapSM.get(chart.ARR_RANGE) || []),
        chart.MARGIN_OPEX_SM
      ]);
    }

    if (chart.MARGIN_OPEX_GA !== null) {
      OpExMapGA.set(chart.ARR_RANGE, [
        ...(OpExMapGA.get(chart.ARR_RANGE) || []),
        chart.MARGIN_OPEX_GA
      ]);
    }

    if (chart.MARGIN_FCF !== null) {
      fcfMarginMap.set(chart.ARR_RANGE, [
        ...(fcfMarginMap.get(chart.ARR_RANGE) || []),
        chart.MARGIN_FCF
      ]);
    }

    if (chart.QUICK_RATIO_ARR !== null) {
      quickRatioMap.set(chart[filterField], [
        ...(quickRatioMap.get(chart[filterField]) || []),
        chart.QUICK_RATIO_ARR
      ]);
    }

    if (chart.EMPLOYEES_PERCTOTAL_RD !== null) {
      headCountDistributionMapRD.set(chart.ARR_RANGE, [
        ...(headCountDistributionMapRD.get(chart.ARR_RANGE) || []),
        chart.EMPLOYEES_PERCTOTAL_RD
      ]);
    }

    if (chart.EMPLOYEES_PERCTOTAL_SM !== null) {
      headCountDistributionMapSM.set(chart.ARR_RANGE, [
        ...(headCountDistributionMapSM.get(chart.ARR_RANGE) || []),
        chart.EMPLOYEES_PERCTOTAL_SM
      ]);
    }

    if (chart.EMPLOYEES_PERCTOTAL_GA !== null) {
      headCountDistributionMapGA.set(chart.ARR_RANGE, [
        ...(headCountDistributionMapGA.get(chart.ARR_RANGE) || []),
        chart.EMPLOYEES_PERCTOTAL_GA
      ]);
    }

    if (chart.ARR_PERCTOTAL_GROSSNEW_NEWLOGO !== null) {
      grossNewARRLogo.set(chart[filterField], [
        ...(grossNewARRLogo.get(chart[filterField]) || []),
        chart.ARR_PERCTOTAL_GROSSNEW_NEWLOGO
      ]);
    }

    if (chart.ARR_PERCTOTAL_GROSSNEW_EXPANSION !== null) {
      grossNewARRExpansion.set(chart[filterField], [
        ...(grossNewARRExpansion.get(chart[filterField]) || []),
        chart.ARR_PERCTOTAL_GROSSNEW_EXPANSION
      ]);
    }

    if (chart.NEW_LOGO_VELOCITY !== null) {
      newLogoVelocityMap.set(chart[filterField], [
        ...(newLogoVelocityMap.get(chart[filterField]) || []),
        chart.NEW_LOGO_VELOCITY
      ]);
    }

    if (chart.OPEX_PER_FTE !== null) {
      opexPerFTEMap.set(chart.ARR_RANGE, [
        ...(opexPerFTEMap.get(chart.ARR_RANGE) || []),
        chart.OPEX_PER_FTE
      ]);
    }

    if (chart.CAC_PAYBACK !== null) {
      cacPaybackMap.set(chart[filterField], [
        ...(cacPaybackMap.get(chart[filterField]) || []),
        chart.CAC_PAYBACK
      ]);
    }

    if (chart.RETENTION_GROSS_ARR_ANNUALIZED !== null) {
      grossDollarRetentionMap.set(chart[filterField], [
        ...(grossDollarRetentionMap.get(chart[filterField]) || []),
        chart.RETENTION_GROSS_ARR_ANNUALIZED
      ]);
    }
  });

  const yourCompanyHeadcountDistribution = {
    gAHeadPercent: adjustPercentage(companyMetrics.gAHeadPercent || 0, companyMetrics.otherHeadPercent || 0),
    rDHeadPercent: adjustPercentage(companyMetrics.rDHeadPercent || 0, companyMetrics.otherHeadPercent || 0),
    sMHeadPercent: adjustPercentage(companyMetrics.sMHeadPercent || 0, companyMetrics.otherHeadPercent || 0),
   // otherHeadPercent: companyMetrics.otherHeadPercent || 0
  };

  const datasets = {
    YoYARRGrowth: [
      {
        label: labelTopQuartile,
        data: mapCalculatedData(
          true,
          arrGrowthMap,
          NSIZE_8
        )?.quartileArray?.map(value => value && value * 100),
        backgroundColor: topQuartileColor
      },
      {
        label: labelMedian,
        data: mapCalculatedData(true, arrGrowthMap, NSIZE_8)?.medianArray?.map(
          value => value && value * 100
        ),
        backgroundColor: medianColor
      },
      !allValuesAreNull(
        yourCompanyFormatted(
          getARRIndex(companyMetrics.currentARR || 0),
          true,
          companyMetrics.yoyARR || 0
        )
      ) && {
        label: labelYourCompany,
        data: yourCompanyFormatted(
          getARRIndex(companyMetrics.currentARR || 0),
          true,
          companyMetrics.yoyARR || 0
        ),
        backgroundColor: companyMetrics.companyColor || yourCompanyColor
      }
    ],
    grossDollarRetention: [
      {
        label: labelTopQuartile,
        data: mapCalculatedData(
          xAxisSwitch,
          grossDollarRetentionMap,
          NSIZE_8
        )?.quartileArray?.map(value => value && value * 100),
        backgroundColor: topQuartileColor
      },
      {
        label: labelMedian,

        data: mapCalculatedData(
          xAxisSwitch,
          grossDollarRetentionMap,
          NSIZE_8
        )?.medianArray?.map(value => value && value * 100),
        backgroundColor: medianColor
      },
      !allValuesAreNull(
        yourCompanyFormatted(
          arrIndex,
          xAxisSwitch,
          companyMetrics.grossDollarRetention || 0
        )
      ) && {
        label: labelYourCompany,
        data: yourCompanyFormatted(
          arrIndex,
          xAxisSwitch,
          companyMetrics.grossDollarRetention || 0
        ),
        backgroundColor: companyMetrics.companyColor || yourCompanyColor
      }
    ],
    netDollarRetention: [
      {
        label: labelTopQuartile,
        data: mapCalculatedData(
          xAxisSwitch,
          netDollarRetentionMap,
          NSIZE_8
        )?.quartileArray?.map(value => value && value * 100),
        backgroundColor: topQuartileColor
      },

      {
        label: labelMedian,
        data: mapCalculatedData(
          xAxisSwitch,
          netDollarRetentionMap,
          NSIZE_8
        )?.medianArray?.map(value => value && value * 100),
        backgroundColor: medianColor
      },
      !allValuesAreNull(
        yourCompanyFormatted(
          arrIndex,
          xAxisSwitch,
          companyMetrics.netDollarRetention || 0
        )
      ) && {
        label: labelYourCompany,
        data: yourCompanyFormatted(
          arrIndex,
          xAxisSwitch,
          companyMetrics.netDollarRetention || 0
        ),
        backgroundColor: companyMetrics.companyColor || yourCompanyColor
      }
    ],
    netMagicNumber: [
      {
        label: labelTopQuartile,
        data: mapCalculatedData(xAxisSwitch, netMagicNumberMap, NSIZE_10)
          .quartileArray,
        backgroundColor: topQuartileColor
      },
      {
        label: labelMedian,
        data: mapCalculatedData(xAxisSwitch, netMagicNumberMap, NSIZE_10)
          .medianArray,
        backgroundColor: medianColor
      },
      !allValuesAreNull(
        yourCompanyFormatted(
          arrIndex,
          xAxisSwitch,
          companyMetrics.netMagicNumber || 0
        )
      ) && {
        label: labelYourCompany,
        data: yourCompanyFormatted(
          arrIndex,
          xAxisSwitch,
          companyMetrics.netMagicNumber || 0
        ),
        backgroundColor: companyMetrics.companyColor || yourCompanyColor
      }
    ],
    grossMargin: [
      {
        label: labelTopQuartile,
        data: mapCalculatedData(
          true,
          grossMarginMap,
          NSIZE_8
        )?.quartileArray?.map(value => value && value * 100),
        backgroundColor: topQuartileColor
      },
      {
        label: labelMedian,
        data: mapCalculatedData(
          true,
          grossMarginMap,
          NSIZE_8
        )?.medianArray?.map(value => value && value * 100),
        backgroundColor: medianColor
      },
      !allValuesAreNull(
        yourCompanyFormatted(
          getARRIndex(companyMetrics.currentARR || 0),
          true,
          companyMetrics.grossMargin || 0
        )
      ) && {
        label: labelYourCompany,
        data: yourCompanyFormatted(
          getARRIndex(companyMetrics.currentARR || 0),
          true,
          companyMetrics.grossMargin || 0
        ),
        backgroundColor: companyMetrics.companyColor || yourCompanyColor
      }
    ],
    burnMultiple: [
      {
        label: labelTopQuartile,
        data: mapCalculatedData(true, burnMultipleMap, NSIZE_8, sortDir.DESC)
          .quartileArray,
        backgroundColor: topQuartileColor
      },
      {
        label: labelMedian,
        data: mapCalculatedData(true, burnMultipleMap, NSIZE_8, sortDir.DESC)
          .medianArray,
        backgroundColor: medianColor
      },
      !allValuesAreNull(
        yourCompanyFormatted(
          getARRIndex(companyMetrics.currentARR || 0),
          true,
          companyMetrics.burnMultiple || 0
        )
      ) && {
        label: labelYourCompany,
        data: yourCompanyFormatted(
          getARRIndex(companyMetrics.currentARR || 0),
          true,
          companyMetrics.burnMultiple || 0
        ),
        backgroundColor: companyMetrics.companyColor || yourCompanyColor
      }
    ],
    ARRperFTE: [
      {
        label: labelTopQuartile,
        data: mapCalculatedData(
          true,
          ARRperFTEMap,
          NSIZE_8
        )?.quartileArray?.map(value => value && Math.round(value / 1000)),
        backgroundColor: topQuartileColor
      },
      {
        label: labelMedian,
        data: mapCalculatedData(true, ARRperFTEMap, NSIZE_8)?.medianArray?.map(
          value => value && Math.round(value / 1000)
        ),
        backgroundColor: medianColor
      },
      !allValuesAreNull(
        yourCompanyFormatted(
          getARRIndex(companyMetrics.currentARR || 0),
          true,
          companyMetrics.arrPerFTE || 0
        )
      ) && {
        label: labelYourCompany,
        data: yourCompanyFormatted(
          getARRIndex(companyMetrics.currentARR || 0),
          true,
          companyMetrics.arrPerFTE || 0,
          true
        ),
        backgroundColor: companyMetrics.companyColor || yourCompanyColor
      }
    ],
    FCFMargin: [
      {
        label: labelTopQuartile,
        data: mapCalculatedData(true, fcfMarginMap, NSIZE_8).quartileArray?.map(
          value => value && value * 100
        ),
        backgroundColor: topQuartileColor
      },
      {
        label: labelMedian,
        data: mapCalculatedData(true, fcfMarginMap, NSIZE_8).medianArray?.map(
          value => value && value * 100
        ),
        backgroundColor: medianColor
      },
      !allValuesAreNull(
        yourCompanyFormatted(
          getARRIndex(companyMetrics.currentARR || 0),
          true,
          companyMetrics.fcfMargin || 0
        )
      ) && {
        label: labelYourCompany,
        data: yourCompanyFormatted(
          getARRIndex(companyMetrics.currentARR || 0),
          true,
          companyMetrics.fcfMargin || 0
        ),
        backgroundColor: companyMetrics.companyColor || yourCompanyColor
      }
    ],
    quickRatio: [
      {
        label: labelTopQuartile,
        data: mapCalculatedData(xAxisSwitch, quickRatioMap, NSIZE_10)
          .quartileArray,
        backgroundColor: topQuartileColor
      },
      {
        label: labelMedian,
        data: mapCalculatedData(xAxisSwitch, quickRatioMap, NSIZE_10)
          .medianArray,
        backgroundColor: medianColor
      },
      !allValuesAreNull(
        yourCompanyFormatted(
          arrIndex,
          xAxisSwitch,
          companyMetrics.quickRatio || 0
        )
      ) && {
        label: labelYourCompany,
        data: yourCompanyFormatted(
          arrIndex,
          xAxisSwitch,
          companyMetrics.quickRatio || 0
        ),
        backgroundColor: companyMetrics.companyColor || yourCompanyColor
      }
    ],
    ruleOf40: [
      {
        label: `${labelTopQuartile} - Bubble size denotes ARR range`,
        data: generateBubbleData(
          mapCalculatedData(true, arrGrowthMap, NSIZE_8)?.quartileArray || [],
          mapCalculatedData(true, fcfMarginMap, NSIZE_8)?.quartileArray || [],
          mapCalculatedData(true, ruleof40Map, NSIZE_8)?.quartileArray || []
        ),
        backgroundColor: 'rgba(77, 185, 107, 0.5)'
      },
      {
        label: labelMedian,
        data: generateBubbleData(
          mapCalculatedData(true, arrGrowthMap, NSIZE_8).medianArray || [],
          mapCalculatedData(true, fcfMarginMap, NSIZE_8).medianArray || [],
          mapCalculatedData(true, ruleof40Map, NSIZE_8)?.medianArray || []
        ),
        backgroundColor: 'rgba(112, 167, 178, 0.5)'
      },
      companyMetrics.yoyARR && {
        label: labelYourCompany,
        data: generateBubbleDataCompany(
          getARRIndex(companyMetrics.currentARR || 0),
          companyMetrics
        ),
        backgroundColor: `${companyMetrics.companyColor || yourCompanyColor}80`
      }
    ],
    newLogoVelocity: [
      {
        label: labelTopQuartile,
        data: mapCalculatedData(
          xAxisSwitch,
          newLogoVelocityMap,
          NSIZE_8
        )?.quartileArray?.map(value => value && value * 100),
        backgroundColor: topQuartileColor
      },
      {
        label: labelMedian,
        data: mapCalculatedData(
          xAxisSwitch,
          newLogoVelocityMap,
          NSIZE_8
        )?.medianArray?.map(value => value && value * 100),
        backgroundColor: medianColor
      },
      !allValuesAreNull(
        yourCompanyFormatted(
          arrIndex,
          xAxisSwitch,
          companyMetrics.newLogoVelocity || 0
        )
      ) && {
        label: labelYourCompany,
        data: yourCompanyFormatted(
          arrIndex,
          xAxisSwitch,
          companyMetrics.newLogoVelocity || 0
        ),
        backgroundColor: companyMetrics.companyColor || yourCompanyColor
      }
    ],
    opexPerFTE: [
      {
        label: labelTopQuartile,
        data: mapCalculatedData(
          true,
          opexPerFTEMap,
          NSIZE_8,
          sortDir.DESC
        )?.quartileArray?.map(value => value && Math.round(value / 1000)),
        backgroundColor: topQuartileColor
      },
      {
        label: labelMedian,
        data: mapCalculatedData(
          true,
          opexPerFTEMap,
          NSIZE_8,
          sortDir.DESC
        )?.medianArray?.map(value => value && Math.round(value / 1000)),
        backgroundColor: medianColor
      },
      !allValuesAreNull(
        yourCompanyFormatted(
          getARRIndex(companyMetrics.currentARR || 0),
          true,
          companyMetrics.opexPerFTE || 0
        )
      ) && {
        label: labelYourCompany,
        data: yourCompanyFormatted(
          getARRIndex(companyMetrics.currentARR || 0),
          true,
          companyMetrics.opexPerFTE || 0,
          true
        ),
        backgroundColor: companyMetrics.companyColor || yourCompanyColor
      }
    ],
    cacPayback: [
      {
        label: labelTopQuartile,
        data: mapCalculatedData(
          xAxisSwitch,
          cacPaybackMap,
          NSIZE_8,
          sortDir.DESC
        )?.quartileArray,
        backgroundColor: topQuartileColor
      },
      {
        label: labelMedian,
        data: mapCalculatedData(
          xAxisSwitch,
          cacPaybackMap,
          NSIZE_8,
          sortDir.DESC
        )?.medianArray,
        backgroundColor: medianColor
      },
      !allValuesAreNull(
        yourCompanyFormatted(
          arrIndex,
          xAxisSwitch,
          companyMetrics.cacPaybackPeriod || 0
        )
      ) && {
        label: labelYourCompany,
        data: yourCompanyFormatted(
          arrIndex,
          xAxisSwitch,
          companyMetrics.cacPaybackPeriod || 0
        ),
        backgroundColor: companyMetrics.companyColor || yourCompanyColor
      }
    ]
  };

  const datasetsStacked = {
    OpEx: [
      [],
      [
        {
          label: `S&M (${labelMedian})`,
          labelComponent: (
            <p>
              <span className='font-bold'>S&M</span> ({labelMedian})
            </p>
          ),
          data: mapCalculatedData(true, OpExMapSM, NSIZE_8).medianArray?.map(
            value => value && value * 100
          ),
          backgroundColor: '#4E7B84',
          stack: 'median'
        },
        {
          label: `R&D (${labelMedian})`,
          labelComponent: (
            <p>
              <span className='font-bold'>R&D</span> ({labelMedian})
            </p>
          ),
          data: mapCalculatedData(true, OpExMapRD, NSIZE_8).medianArray?.map(
            value => value && value * 100
          ),
          backgroundColor: '#70A7B2',
          stack: 'median'
        },
        {
          label: `G&A (${labelMedian})`,
          labelComponent: (
            <p>
              <span className='font-bold'>G&A</span> ({labelMedian})
            </p>
          ),
          data: mapCalculatedData(true, OpExMapGA, NSIZE_8).medianArray?.map(
            value => value && value * 100
          ),
          backgroundColor: '#8ABBC5',
          stack: 'median'
        }
      ],
      [
        !allValuesAreNull(
          yourCompanyFormatted(
            getARRIndex(companyMetrics.currentARR || 0),
            true,
            companyMetrics.smPercent || 0
          )
        ) && {
          label: `${labelYourCompany} (S&M)`,
          data: yourCompanyFormatted(
            getARRIndex(companyMetrics.currentARR || 0),
            true,
            companyMetrics.smPercent || 0
          ),
          backgroundColor: `${companyMetrics.companyColor || yourCompanyColor}`,
          stack: 'Your Company Data'
        },
        !allValuesAreNull(
          yourCompanyFormatted(
            getARRIndex(companyMetrics.currentARR || 0),
            true,
            companyMetrics.rdPercent || 0
          )
        ) && {
          label: `${labelYourCompany} (R&D)`,
          data: yourCompanyFormatted(
            getARRIndex(companyMetrics.currentARR || 0),
            true,
            companyMetrics.rdPercent || 0
          ),
          backgroundColor: `${
            companyMetrics.companyColor || yourCompanyColor
          }CC`,
          stack: 'Your Company Data'
        },
        !allValuesAreNull(
          yourCompanyFormatted(
            getARRIndex(companyMetrics.currentARR || 0),
            true,
            companyMetrics.gaPercent || 0
          )
        ) && {
          label: `${labelYourCompany} (G&A)`,
          data: yourCompanyFormatted(
            getARRIndex(companyMetrics.currentARR || 0),
            true,
            companyMetrics.gaPercent || 0
          ),
          backgroundColor: `${
            companyMetrics.companyColor || yourCompanyColor
          }80`,
          stack: 'Your Company Data'
        }
      ]
    ],
    headcountDistribution: [
      [
        {
          label: `S&M (${labelAverage})`,
          labelComponent: (
            <p>
              <span className='font-bold'>S&M</span> ({labelAverage})
            </p>
          ),
          data: mapCalculatedData(
            true,
            headCountDistributionMapSM,
            NSIZE_8
          ).averageArray?.map(value => value && value * 100),
          backgroundColor: '#14662F'
        },
        {
          label: `R&D (${labelAverage})`,
          labelComponent: (
            <p>
              <span className='font-bold'>R&D</span> ({labelAverage})
            </p>
          ),
          data: mapCalculatedData(
            true,
            headCountDistributionMapRD,
            NSIZE_8
          ).averageArray?.map(value => value && value * 100),
          backgroundColor: '#409958'
        },
        {
          label: `G&A (${labelAverage})`,
          labelComponent: (
            <p>
              <span className='font-bold'>G&A</span> ({labelAverage})
            </p>
          ),
          data: mapCalculatedData(
            true,
            headCountDistributionMapGA,
            NSIZE_8
          ).averageArray?.map(value => value && value * 100),
          backgroundColor: '#4DB96B'
        }
      ],
      [],
      [
        !allValuesAreNull(
          yourCompanyFormatted(
            getARRIndex(companyMetrics.currentARR || 0),
            true,
            yourCompanyHeadcountDistribution.sMHeadPercent
          )
        ) && {
          label: "Your Company's Data (S&M)",
          data: yourCompanyFormatted(
            getARRIndex(companyMetrics.currentARR || 0),
            true,
            yourCompanyHeadcountDistribution.sMHeadPercent
          ),
          backgroundColor: `${companyMetrics.companyColor || yourCompanyColor}`,
          stack: 'Your Company Data'
        },
        !allValuesAreNull(
          yourCompanyFormatted(
            getARRIndex(companyMetrics.currentARR || 0),
            true,
            yourCompanyHeadcountDistribution.rDHeadPercent
          )
        ) && {
          label: "Your Company's Data (R&D)",
          data: yourCompanyFormatted(
            getARRIndex(companyMetrics.currentARR || 0),
            true,
            yourCompanyHeadcountDistribution.rDHeadPercent
          ),
          backgroundColor: `${
            companyMetrics.companyColor || yourCompanyColor
          }CC`,
          stack: 'Your Company Data'
        },
        !allValuesAreNull(
          yourCompanyFormatted(
            getARRIndex(companyMetrics.currentARR || 0),
            true,
            yourCompanyHeadcountDistribution.gAHeadPercent
          )
        ) && {
          label: "Your Company's Data (G&A)",
          data: yourCompanyFormatted(
            getARRIndex(companyMetrics.currentARR || 0),
            true,
            yourCompanyHeadcountDistribution.gAHeadPercent
          ),
          backgroundColor: `${
            companyMetrics.companyColor || yourCompanyColor
          }80`,
          stack: 'Your Company Data'
        }
      ]
    ],
    grossNewARRComposition: [
      [],
      [
        {
          label: `New Logo ARR as a % of Gross New (${labelAverage})`,
          labelComponent: (
            <p>
              <span className='font-bold'>
                New Logo ARR as a % of Gross New
              </span>{' '}
              ({labelAverage})
            </p>
          ),
          data: mapCalculatedData(
            xAxisSwitch,
            grossNewARRLogo,
            NSIZE_8
          ).averageArray.map(value => value && value * 100),
          backgroundColor: '#14662F',
          stack: 'average'
        },
        {
          label: `Expansion ARR as a % of Gross New (${labelAverage})`,
          labelComponent: (
            <p>
              <span className='font-bold'>
                Expansion ARR as a % of Gross New
              </span>{' '}
              ({labelAverage})
            </p>
          ),
          data: mapCalculatedData(
            xAxisSwitch,
            grossNewARRExpansion,
            NSIZE_8
          ).averageArray.map(value => value && value * 100),
          backgroundColor: '#409958',
          stack: 'average'
        }
      ],
      [
        !allValuesAreNull(
          yourCompanyFormatted(
            arrIndex,
            xAxisSwitch,
            companyMetrics.newLogoArrOfGrossNew || 0
          )
        ) && {
          label: "Your Company's New Logo ARR",
          data: yourCompanyFormatted(
            arrIndex,
            xAxisSwitch,
            companyMetrics.newLogoArrOfGrossNew || 0
          ),
          backgroundColor: `${companyMetrics.companyColor || yourCompanyColor}`,
          stack: 'Your Company Data'
        },
        !allValuesAreNull(
          yourCompanyFormatted(
            arrIndex,
            xAxisSwitch,
            companyMetrics.expansionArrOfGrossNew || 0
          )
        ) && {
          label: "Your Company's Expansion ARR",
          data: yourCompanyFormatted(
            arrIndex,
            xAxisSwitch,
            companyMetrics.expansionArrOfGrossNew || 0
          ),
          backgroundColor: `${
            companyMetrics.companyColor || yourCompanyColor
          }CC`,
          stack: 'Your Company Data'
        }
      ]
    ]
  };
  return { datasets: datasets, datasetsStacked: datasetsStacked };
};
